import { clearBookingConfigCache } from '@/actions/booking-config-action';
import { authLogin, authLoginWithToken, logout, updateUser } from '@/actions/user-action';
import logger from '@/lib/edge-logger';
import { LoginCredential, LoginToken } from '@/types/frontend';
import { User } from '@/types/user';
import { clearAuthCookie, setAuthCookie } from '@/utils/cookie-util';
import { message } from 'antd';
import { pick } from 'lodash';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// ==================== 用户偏好类型定义 ====================

export interface ExportColumnsPreference {
  selectedColumns: string[];
  allColumns: string[];
  /** 导出列的自定义顺序（value 数组，含未勾选列；缺省时按默认列顺序） */
  columnOrder?: string[];
  lastModified?: string;
  version?: string;
  lastSync?: string;
}

export interface ListColumnsPreference {
  columnsState: Record<string, any>;
  lastModified?: string;
  lastSync?: string;
}

export interface UserPreferences {
  exportColumns: Record<string, ExportColumnsPreference>;
  listColumns: Record<string, ListColumnsPreference>;
}

interface UserStore {
  user: User | null;
  token: LoginToken | null;
  pathPermissions: string[];
  preferences: UserPreferences;
  login: (credentials: LoginCredential) => Promise<User | null>;
  loginWithToken: (token: string) => Promise<User | null>;
  logout: () => Promise<void>;
  updateUser: (updates: Partial<User>) => Promise<void>;
  refreshToken: (updates: Partial<LoginToken>) => User | null;
  // 导出列偏好方法
  getExportColumnsPreference: (code: string) => ExportColumnsPreference | null;
  setExportColumnsPreference: (code: string, preference: ExportColumnsPreference) => void;
  clearExportColumnsPreference: (code: string) => void;
  clearAllExportColumnsPreferences: () => void;
  // 列表列偏好方法
  getListColumnsPreference: (code: string) => ListColumnsPreference | null;
  setListColumnsPreference: (code: string, preference: ListColumnsPreference) => void;
  clearListColumnsPreference: (code: string) => void;
}

type SetState<T> = {
  (partial: T | Partial<T> | ((state: T) => T | Partial<T>), replace?: false): void;
  (state: T | ((state: T) => T), replace: true): void;
};

const handleLoginStatus = ({ user, token, pathPermissions }: { user: User | null; token: LoginToken | null; pathPermissions?: string[] }, set: any, setAuthCookie: any): User | null => {
  set({ user, token, pathPermissions: pathPermissions || [] });
  if (token?.token) {
    setAuthCookie(token.token, token.expireAt);
  } else {
    message.error('登录失败');
  }
  return user;
};

function doLogin(set: SetState<UserStore>): (credentials: LoginCredential) => Promise<User | null> {
  return async (credentials) => {
    const { user, token, pathPermissions } = await authLogin(credentials);
    return handleLoginStatus({ user, token, pathPermissions }, set, setAuthCookie);
  };
}

function dologinWithToken(set: SetState<UserStore>): (tokenFromUrl: string) => Promise<User | null> {
  return async (tokenFromUrl) => {
    const { user, token, pathPermissions } = await authLoginWithToken(tokenFromUrl);
    return handleLoginStatus({ user, token, pathPermissions }, set, setAuthCookie);
  };
}

function doLogout(set: SetState<UserStore>): () => Promise<void> {
  return async () => {
    const currentState = useUserStore.getState();
    if (currentState.user?.account) {
      try {
        // 调用API清除Redis缓存
        await logout(currentState.user.account, currentState.user.id as number);
      } catch (error) {
        logger.error('清除用户缓存失败:', error);
      }
    }
    // 清除所有本地缓存
    clearBookingConfigCache();
    clearAuthCookie();
    // 重置 store 状态
    set({ user: null, token: null, pathPermissions: [], preferences: undefined });
  };
}

// ==================== 偏好操作方法 ====================

function doGetExportColumnsPreference(set: SetState<UserStore>, get: () => UserStore): (code: string) => ExportColumnsPreference | null {
  return (code) => {
    const state = get();
    return state.preferences?.exportColumns?.[code] || null;
  };
}

function doSetExportColumnsPreference(set: SetState<UserStore>, get: () => UserStore): (code: string, preference: ExportColumnsPreference) => void {
  return (code, preference) => {
    set((state) => ({
      preferences: {
        ...(state.preferences || initialPreferences),
        exportColumns: {
          ...((state.preferences?.exportColumns) || {}),
          [code]: preference
        }
      }
    }));
  };
}

function doClearExportColumnsPreference(set: SetState<UserStore>): (code: string) => void {
  return (code) => {
    set((state) => {
      const exportColumns = state.preferences?.exportColumns || {};
      const { [code]: _, ...rest } = exportColumns;
      return {
        preferences: {
          ...(state.preferences || initialPreferences),
          exportColumns: rest
        }
      };
    });
  };
}

function doClearAllExportColumnsPreferences(set: SetState<UserStore>): () => void {
  return () => {
    set((state) => ({
      preferences: {
        ...(state.preferences || initialPreferences),
        exportColumns: {}
      }
    }));
  };
}

function doGetListColumnsPreference(set: SetState<UserStore>, get: () => UserStore): (code: string) => ListColumnsPreference | null {
  return (code) => {
    const state = get();
    if (!state.preferences) return null;
    return state.preferences.listColumns[code] || null;
  };
}

function doSetListColumnsPreference(set: SetState<UserStore>): (code: string, preference: ListColumnsPreference) => void {
  return (code, preference) => {
    set((state) => ({
      preferences: {
        ...(state.preferences || initialPreferences),
        listColumns: {
          ...((state.preferences?.listColumns) || {}),
          [code]: preference
        }
      }
    }));
  };
}

function doClearListColumnsPreference(set: SetState<UserStore>): (code: string) => void {
  return (code) => {
    set((state) => {
      const listColumns = state.preferences?.listColumns || {};
      const { [code]: _, ...rest } = listColumns;
      return {
        preferences: {
          ...(state.preferences || initialPreferences),
          listColumns: rest
        }
      };
    });
  };
}

function doRefreshToken(set: SetState<UserStore>): (updates: Partial<LoginToken>) => User | null {
  return (updates) => {
    // 保留当前用户信息，只更新token
    const currentState = useUserStore.getState();
    const updatedToken = { ...currentState.token, ...updates };
    // 确保传递有效的User对象给handleLoginStatus
    const user = currentState.user || null;
    return handleLoginStatus({ user, token: updatedToken }, set, setAuthCookie);
  };
}

function doUpdateUser(set: SetState<UserStore>): (updates: Partial<User>) => Promise<void> {
  return async (updates) => {
    try {
      // 编辑信息只修改 姓名、手机、邮箱
      updates = pick(updates, ['id', 'name', 'mobile', 'email']);
      await updateUser(updates);
      set((state) => ({
        user: state.user ? { ...state.user, ...updates } : null,
        token: state.token ? state.token : null
      }));
    } catch (error) {
      logger.error('更新用户信息失败:', error);
    }
  };
}

const initialPreferences: UserPreferences = {
  exportColumns: {},
  listColumns: {}
};

export const useUserStore = create<UserStore>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      pathPermissions: [],
      preferences: initialPreferences,
      login: doLogin(set),
      loginWithToken: dologinWithToken(set),
      logout: doLogout(set),
      updateUser: doUpdateUser(set),
      refreshToken: doRefreshToken(set),
      // 导出列偏好方法
      getExportColumnsPreference: doGetExportColumnsPreference(set, get),
      setExportColumnsPreference: doSetExportColumnsPreference(set, get),
      clearExportColumnsPreference: doClearExportColumnsPreference(set),
      clearAllExportColumnsPreferences: doClearAllExportColumnsPreferences(set),
      // 列表列偏好方法
      getListColumnsPreference: doGetListColumnsPreference(set, get),
      setListColumnsPreference: doSetListColumnsPreference(set),
      clearListColumnsPreference: doClearListColumnsPreference(set),
    }),
    {
      name: 'user-storage',
    }
  )
);

// 检查用户是否有指定权限
export const hasPermission = (permissionCode: string) => {
  const user = useUserStore.getState().user;
  if (!user || !user.permissioned || !user.permissions) {
    return false;
  }
  const perms = user.permissions;
  // 登录接口将权限扁平化为字符串数组，但 User 类型声明为 Record 以保持兼容性
  if (Array.isArray(perms)) {
    return perms.includes(permissionCode);
  }
  return false;
};